// Optimized Variant Slider with Image Change and Cart Functionality
(function() {
  'use strict';

  // Cache DOM queries to improve performance
  const domCache = new Map();
  
  function getCachedElement(selector) {
    if (!domCache.has(selector)) {
      domCache.set(selector, document.querySelector(selector));
    }
    return domCache.get(selector);
  }

  function getCachedElements(selector) {
    if (!domCache.has(selector + '_all')) {
      domCache.set(selector + '_all', document.querySelectorAll(selector));
    }
    return domCache.get(selector + '_all');
  }

  // Debounce function for performance
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Currency formatting function - matches Shopify's {{ price | money }} Liquid filter
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace(/,/g, '');
    }
    
    // Get Shopify's money format from theme settings (same as Liquid {{ price | money }})
    let formatString = format;
    if (!formatString) {
      // Try to get format from Shopify theme settings
      formatString = window.theme?.moneyFormat || 
                     window.Shopify?.money_format || 
                     window.shopSettings?.moneyFormat ||
                     '${{amount}}'; // Fallback
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';
      
      if (isNaN(number) || number === null) {
        return '0.00';
      }
      
      number = (number / 100.0).toFixed(precision);
      
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      
      return dollarsAmount + centsAmount;
    }
    
    const match = formatString.match(placeholderRegex);
    if (!match) {
      return formatString; // Return as-is if no placeholder found
    }
    
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    
    return formatString.replace(placeholderRegex, value);
  }

  // Optimized cart update function
  function updateCartCount(count) {
    const cartCountSelectors = [
      '.cart-link__count',
      '.cart-drawer__title-count'
    ];
    
    cartCountSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.textContent = count;
        
        // Hide cart count elements when cart is empty
        if (count > 0) {
          element.style.display = 'inline-block';
          element.style.visibility = 'visible';
          element.classList.remove('hidden');
          element.classList.add('visible');
        } else {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.classList.add('hidden');
          element.classList.remove('visible');
        }
        
        element.setAttribute('data-cart-count', count);
      });
    });
    
    // Specifically handle .cart-link__count elements for complete hiding
    const cartLinkCounts = document.querySelectorAll('.cart-link__count');
    cartLinkCounts.forEach(countElement => {
      if (count === 0) {
        countElement.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
        countElement.setAttribute('aria-hidden', 'true');
      } else {
        countElement.style.cssText = 'display: inline-block !important; visibility: visible !important; opacity: 1 !important;';
        countElement.removeAttribute('aria-hidden');
      }
    });
    
    // Also handle parent cart link elements
    const cartLinks = document.querySelectorAll('.cart-link, .header-cart, .site-header__cart');
    cartLinks.forEach(cartLink => {
      const countElement = cartLink.querySelector('.cart-link__count, [data-cart-count]');
      if (countElement) {
        if (count > 0) {
          cartLink.classList.add('cart-has-items');
          cartLink.classList.remove('cart-empty');
        } else {
          cartLink.classList.remove('cart-has-items');
          cartLink.classList.add('cart-empty');
        }
      }
    });
  }

  // Quick cart drawer open using proper cart drawer events
  function openCartDrawer() {
    // Use the cart drawer's built-in open event (most reliable)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      return;
    }
    
    // Try theme-specific methods as fallback
    if (window.theme?.Drawers?.openCart) {
      window.theme.Drawers.openCart();
      return;
    }
    if (window.theme?.Drawers?.open) {
      window.theme.Drawers.open('cart');
      return;
    }
    
    // Click cart trigger as another fallback
    const trigger = document.querySelector('[data-drawer-trigger="cart"], .js-drawer-open-cart, .cart-link, .site-header__cart-toggle');
    if (trigger) {
      trigger.click();
      return;
    }
    
    // Direct manipulation as last resort
    const drawer = document.querySelector('[data-drawer="cart"], .drawer--cart, #CartDrawer');
    if (drawer) {
      drawer.classList.add('is-open', 'drawer--is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open', 'no-scroll');
    }
  }

  // Optimized cart update using cart drawer's built-in refresh
  async function quickCartUpdate() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      console.log('Cart updated with items:', cart.item_count);
      
      // Update count immediately
      updateCartCount(cart.item_count);
      
      // Use cart drawer's built-in refresh mechanism
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Trigger the cart drawer's refresh event
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        
        // Also trigger the cart form refresh directly
        const cartForm = cartDrawer.querySelector('cart-form');
        if (cartForm && typeof cartForm.refresh === 'function') {
          cartForm.refresh();
        }
      }
      
      // Fallback: Update drawer if using theme's AJAX cart
      if (window.ajaxCart?.load) {
        window.ajaxCart.load();
      } else if (window.ajaxCart?.updateView) {
        window.ajaxCart.updateView();
      }
      
      // Dispatch events for theme compatibility
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      
      return cart;
    } catch (error) {
      console.error('Cart update error:', error);
      return null;
    }
  }

  // Function to change product images when variant is selected
  // Uses theme class-based visibility (--active, --show-on-hover) so hover image swap still works
  function updateProductImage(container, variantImageId) {
    const productBlock = container.closest('product-block, .product-block');
    if (!productBlock) return;

    const imageCont = productBlock.querySelector('.image-cont');
    if (!imageCont) return;

    const prevArrow = imageCont.querySelector('.image-page-button--previous');
    const nextArrow = imageCont.querySelector('.image-page-button--next');

    if (!variantImageId) {
      if (prevArrow) {
        prevArrow.style.display = 'none';
        prevArrow.setAttribute('aria-hidden', 'true');
      }
      if (nextArrow) {
        nextArrow.style.display = 'none';
        nextArrow.setAttribute('aria-hidden', 'true');
      }
      return;
    }

    const imageWrapper = imageCont.querySelector('.image-label-wrap > div') || imageCont.querySelector('.image-label-wrap') || imageCont;
    const allImages = Array.from(imageWrapper.querySelectorAll('.product-block__image:not(.product-block__image--variant-hidden)'));

    if (allImages.length === 0) return;

    const targetIndex = allImages.findIndex(img =>
      img.dataset.mediaId === variantImageId || img.dataset.mediaId === String(variantImageId)
    );

    if (targetIndex === -1) return;

    const targetImage = allImages[targetIndex];
    const hoverIndex = (targetIndex + 1) % allImages.length;
    const hoverImage = allImages[hoverIndex];

    // Remove inline styles so CSS hover rules can apply
    allImages.forEach(img => img.removeAttribute('style'));

    // Remove all active/hover states and inactivated
    allImages.forEach(img => {
      img.classList.remove('product-block__image--active', 'product-block__image--show-on-hover', 'product-block__image--inactivated');
    });

    // Set active image (visible by default)
    targetImage.classList.add('product-block__image--active');

    // Set hover image (shown on .image-cont:hover) so theme hover still works
    hoverImage.classList.add('product-block__image--show-on-hover');
    hoverImage.classList.remove('product-block__image--inactivated');

    // Update image dots
    const imageDots = imageCont.querySelectorAll('.product-block__image-dot');
    imageDots.forEach(dot => dot.classList.remove('product-block__image-dot--active'));
    if (imageDots[targetIndex]) imageDots[targetIndex].classList.add('product-block__image-dot--active');

    // Restore arrow visibility (remove any forced hide from previous run)
    if (prevArrow) {
      prevArrow.removeAttribute('style');
      prevArrow.removeAttribute('aria-hidden');
      prevArrow.classList.remove('disabled-for-variant');
    }
    if (nextArrow) {
      nextArrow.removeAttribute('style');
      nextArrow.removeAttribute('aria-hidden');
      nextArrow.classList.remove('disabled-for-variant');
    }
  }

  // Main initialization
  function initVariantSlider() {
    const containers = document.querySelectorAll('.variant-slider-container');
    
    containers.forEach(container => {
      const productId = container.dataset.productId;
      const moneyFormat = container.dataset.moneyFormat || '${{amount}}'; // Get Shopify's money format
      const variantInput = container.querySelector('[data-variant-input]');
      const selectedTitle = container.querySelector('[data-selected-title]');
      const priceCurrent = container.querySelector('[data-price-current]');
      const priceCompare = container.querySelector('[data-price-compare]');
      const addToCartBtn = container.querySelector('[data-add-to-cart-btn]');
      const addToCartForm = container.querySelector('[data-product-form]');
      const btnText = addToCartBtn?.querySelector('.btn-text');
      const btnLoading = addToCartBtn?.querySelector('.btn-loading');
      
      // Handle variant selection with image change
      const variantPills = container.querySelectorAll('.variant-pill');
      
      variantPills.forEach(pill => {
        pill.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          // Don't select unavailable variants
          if (this.dataset.variantAvailable === 'false') return;
          
          // Auto-scroll to show full pill if partially hidden
          const track = pill.closest('.variant-slider-track');
          if (track) {
            const pillRect = pill.getBoundingClientRect();
            const trackRect = track.getBoundingClientRect();
            
            // Check if pill is partially hidden on the left
            if (pillRect.left < trackRect.left) {
              const scrollAmount = pill.offsetLeft - track.scrollLeft - 8; // 8px padding
              track.scrollTo({ left: scrollAmount, behavior: 'smooth' });
            }
            // Check if pill is partially hidden on the right
            else if (pillRect.right > trackRect.right) {
              const scrollAmount = pill.offsetLeft - track.clientWidth + pill.offsetWidth + 8; // 8px padding
              track.scrollTo({ left: scrollAmount, behavior: 'smooth' });
            }
          }
          
          // Update selection state
          variantPills.forEach(p => p.removeAttribute('data-selected'));
          this.setAttribute('data-selected', 'true');
          
          // Get variant data
          const variantId = this.dataset.variantId;
          const variantTitle = this.dataset.variantTitle;
          const variantPrice = this.dataset.variantPrice;
          const variantComparePrice = this.dataset.variantComparePrice;
          const variantImageId = this.dataset.variantImageId;
          const isAvailable = this.dataset.variantAvailable === 'true';
          
          // Update form input
          if (variantInput) variantInput.value = variantId;
          
          // Update displayed information
          if (selectedTitle) selectedTitle.textContent = variantTitle;
          if (priceCurrent) {
            // Format price properly with currency using Shopify's money format
            priceCurrent.textContent = formatMoney(variantPrice, moneyFormat);
          }
          
          if (priceCompare) {
            if (variantComparePrice && variantComparePrice !== variantPrice) {
              // Format compare price properly with currency using Shopify's money format
              priceCompare.textContent = formatMoney(variantComparePrice, moneyFormat);
              priceCompare.style.display = 'inline';
            } else {
              priceCompare.style.display = 'none';
            }
          }
          
          // Update button state
          if (addToCartBtn && btnText) {
            if (isAvailable) {
              addToCartBtn.disabled = false;
              btnText.textContent = 'ADD TO BAG';
            } else {
              addToCartBtn.disabled = true;
              btnText.textContent = 'SOLD OUT';
            }
          }
          
          // Update product image - NEW FEATURE
          if (variantImageId) {
            updateProductImage(container, variantImageId);
          }
        });
      });
      
      // Optimized form submission
      if (addToCartForm) {
        addToCartForm.addEventListener('submit', async function(e) {
          e.preventDefault();
          
          if (!addToCartBtn || addToCartBtn.disabled) return;
          
          // Show loading state
          if (btnText) btnText.style.display = 'none';
          if (btnLoading) btnLoading.style.display = 'flex';
          addToCartBtn.disabled = true;
          
          const formData = new FormData(this);
          
          try {
            // Add to cart
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              body: formData
            });
            
            const data = await response.json();
            
            // Quick success feedback
            if (btnText) {
              btnText.style.display = 'block';
              btnText.textContent = 'ADDED!';
            }
            if (btnLoading) btnLoading.style.display = 'none';
            
            // Update cart and open drawer immediately
            await quickCartUpdate();
            openCartDrawer();
            
            // Reset button after delay
            setTimeout(() => {
              if (btnText) btnText.textContent = 'ADD TO BAG';
              if (addToCartBtn) addToCartBtn.disabled = false;
            }, 1500);
            
          } catch (error) {
            console.error('Add to cart error:', error);
            
            // Show error state
            if (btnText) {
              btnText.style.display = 'block';
              btnText.textContent = 'ERROR';
            }
            if (btnLoading) btnLoading.style.display = 'none';
            
            setTimeout(() => {
              if (btnText) btnText.textContent = 'ADD TO BAG';
              if (addToCartBtn) addToCartBtn.disabled = false;
            }, 2000);
          }
        });
      }
      
      // Slider navigation
      const prevBtn = container.querySelector('.variant-nav--prev');
      const nextBtn = container.querySelector('.variant-nav--next');
      const track = container.querySelector('.variant-slider-track');
      
      if (prevBtn && nextBtn && track) {
        // Optimized scroll handling
        const getScrollAmount = () => {
          const pill = track.querySelector('.variant-pill');
          return pill ? pill.offsetWidth + 8 : 120;
        };
        
        prevBtn.addEventListener('click', () => {
          track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
        });
        
        nextBtn.addEventListener('click', () => {
          track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
        });
        
        // Debounced navigation update for better performance
        const updateNavigation = debounce(() => {
          const canScrollLeft = track.scrollLeft > 5;
          const canScrollRight = track.scrollLeft < (track.scrollWidth - track.clientWidth - 5);
          
          prevBtn.style.opacity = canScrollLeft ? '1' : '0.3';
          nextBtn.style.opacity = canScrollRight ? '1' : '0.3';
          prevBtn.style.pointerEvents = canScrollLeft ? 'auto' : 'none';
          nextBtn.style.pointerEvents = canScrollRight ? 'auto' : 'none';
          
          const needsNavigation = track.scrollWidth > track.clientWidth;
          prevBtn.style.display = needsNavigation ? 'flex' : 'none';
          nextBtn.style.display = needsNavigation ? 'flex' : 'none';
        }, 50);
        
        track.addEventListener('scroll', updateNavigation);
        window.addEventListener('resize', updateNavigation);
        
        // Initial check
        setTimeout(updateNavigation, 100);
      }
    });
  }

  // PLP hover image: show second image when hovering product card (JS-driven for reliability)
  const HOVER_CLASS = 'plp-image-hover';
  let plpHoverInitialized = false;
  function initPlpHoverImage() {
    if (plpHoverInitialized) return;
    plpHoverInitialized = true;
    document.addEventListener('mouseover', function(e) {
      const imageCont = e.target.closest('.image-cont--with-secondary-image');
      if (!imageCont) return;
      imageCont.classList.add(HOVER_CLASS);
    }, true);
    document.addEventListener('mouseout', function(e) {
      const imageCont = e.target.closest('.image-cont--with-secondary-image');
      if (!imageCont) return;
      const related = e.relatedTarget;
      if (related && imageCont.contains(related)) return;
      imageCont.classList.remove(HOVER_CLASS);
    }, true);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initVariantSlider();
      initPlpHoverImage();
    });
  } else {
    initVariantSlider();
    initPlpHoverImage();
  }

  // Reinitialize on dynamic content load (for AJAX loaded products)
  document.addEventListener('shopify:section:load', function() {
    initVariantSlider();
    initPlpHoverImage();
  });
  document.addEventListener('product:loaded', function() {
    initVariantSlider();
    initPlpHoverImage();
  });
})();